import React, { createContext, useContext, useCallback, useRef, useEffect, useMemo } from 'react';

import { CONFIG } from '@/constants/config';

export interface ShortcutDefinition {
  key: string;
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  scope: 'global' | 'timesheet';
  label: string;
  description: string;
  handler: () => void;
  sequence?: string;
}

interface ShortcutContextValue {
  register: (shortcuts: ShortcutDefinition[]) => () => void;
  getAll: () => ShortcutDefinition[];
}

const ShortcutContext = createContext<ShortcutContextValue | null>(null);

interface ShortcutProviderProps {
  children: React.ReactNode;
}

export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({ children }) => {
  const registryRef = useRef<Set<ShortcutDefinition[]>>(new Set());
  const pendingSequenceRef = useRef<string | null>(null);
  const sequenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const register = useCallback((shortcuts: ShortcutDefinition[]): (() => void) => {
    registryRef.current.add(shortcuts);
    return () => {
      registryRef.current.delete(shortcuts);
    };
  }, []);

  const getAll = useCallback((): ShortcutDefinition[] => {
    const all: ShortcutDefinition[] = [];
    registryRef.current.forEach(group => {
      group.forEach(s => all.push(s));
    });
    return all;
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent): void => {
      const target = e.target;
      const isInput = target instanceof HTMLElement
        && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT'
        || target.isContentEditable);

      const allShortcuts = getAll();

      // Check for sequence completion
      if (pendingSequenceRef.current) {
        const seqKey = pendingSequenceRef.current;
        pendingSequenceRef.current = null;
        if (sequenceTimerRef.current) {
          clearTimeout(sequenceTimerRef.current);
          sequenceTimerRef.current = null;
        }

        const match = allShortcuts.find(s =>
          s.sequence === seqKey && s.key.toLowerCase() === e.key.toLowerCase()
          && !s.ctrl && !s.alt && !s.shift
        );
        if (match) {
          e.preventDefault();
          match.handler();
          return;
        }
      }

      // Check for sequence starters (e.g., 'g' starts G->D, G->T, etc.)
      if (!isInput && !e.ctrlKey && !e.altKey && !e.shiftKey) {
        const hasSequence = allShortcuts.some(
          s => s.sequence?.toLowerCase() === e.key.toLowerCase()
        );
        if (hasSequence) {
          pendingSequenceRef.current = e.key.toLowerCase();
          sequenceTimerRef.current = setTimeout(() => {
            pendingSequenceRef.current = null;
          }, CONFIG.SHORTCUT_SEQUENCE_TIMEOUT_MS);
          return;
        }
      }

      // Check for ? shortcut (ignore when in input fields)
      if (!isInput && e.key === '?' && !e.ctrlKey && !e.altKey) {
        const match = allShortcuts.find(s => s.key === '?' && !s.ctrl && !s.alt);
        if (match) {
          e.preventDefault();
          match.handler();
          return;
        }
      }

      // Standard modifier shortcuts
      for (const shortcut of allShortcuts) {
        if (shortcut.sequence) continue;

        const ctrlMatch = !!shortcut.ctrl === (e.ctrlKey || e.metaKey);
        const altMatch = !!shortcut.alt === e.altKey;
        const shiftMatch = !!shortcut.shift === e.shiftKey;
        const keyMatch = shortcut.key.toLowerCase() === e.key.toLowerCase()
          || shortcut.key === e.key;

        if (keyMatch && ctrlMatch && altMatch && shiftMatch) {
          // Skip non-modifier shortcuts when in input (except ? handled above)
          if (isInput && !shortcut.ctrl && !shortcut.alt) continue;

          e.preventDefault();
          shortcut.handler();
          return;
        }
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [getAll]);

  const contextValue = useMemo(() => ({ register, getAll }), [register, getAll]);

  return (
    <ShortcutContext.Provider value={contextValue}>
      {children}
    </ShortcutContext.Provider>
  );
};

export const useShortcuts = (): ShortcutContextValue => {
  const ctx = useContext(ShortcutContext);
  if (!ctx) throw new Error('useShortcuts must be used within ShortcutProvider');
  return ctx;
};
